import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Plus, RotateCcw, Save, Star, Trash2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MAX_GALLERY = 8;
const MAX_TESTIMONIALS = 12;
const MAX_CS = 6;

const Head = ({ title, desc, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      {desc && <p className="mt-1 max-w-xl text-xs text-muted-foreground">{desc}</p>}
    </div>
    {children}
  </div>
);

export default function LandingSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [logo, setLogo] = useState("");
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [csNumbers, setCsNumbers] = useState([]);
  const logoRef = useRef(null);
  const galRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/landing");
      setLogo(data.logo || "");
      setGallery(data.gallery || []);
      setTestimonials(data.testimonials || []);
      setCsNumbers(data.cs_numbers || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(kind);
    try {
      const { data } = await api.post(`/settings/landing/image?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } catch (e) {
      toast.error(errMsg(e));
      return "";
    } finally {
      setUploading("");
    }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await upload(file, "logo");
    if (url) { setLogo(url); toast.success("Logo diunggah, jangan lupa Simpan"); }
    if (logoRef.current) logoRef.current.value = "";
  };

  const onGalleryPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_GALLERY - gallery.length;
    if (room <= 0) { toast.error(`Maksimal ${MAX_GALLERY} gambar`); return; }
    const added = [];
    for (const f of files.slice(0, room)) {
      const url = await upload(f, "gallery");
      if (url) added.push({ title: f.name.replace(/\.[^.]+$/, "").slice(0, 40), image: url });
    }
    if (added.length) { setGallery((g) => [...g, ...added]); toast.success(`${added.length} gambar ditambahkan, jangan lupa Simpan`); }
    if (galRef.current) galRef.current.value = "";
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/landing", {
        logo,
        gallery: gallery.map((g) => ({ title: g.title || "", image: g.image })),
        testimonials: testimonials.map((t) => ({
          name: t.name || "", city: t.city || "", text: t.text || "", rating: Number(t.rating || 5),
        })),
        cs_numbers: csNumbers.map((c) => ({ name: c.name || "", number: c.number || "" })),
      });
      setLogo(data.logo || "");
      setGallery(data.gallery || []);
      setTestimonials(data.testimonials || []);
      setCsNumbers(data.cs_numbers || []);
      toast.success("Konten landing page disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Kembalikan testimoni & galeri ke contoh bawaan?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/landing/reset");
      setGallery(data.gallery || []);
      setTestimonials(data.testimonials || []);
      toast.success("Konten dikembalikan ke contoh bawaan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Memuat konten landing page…</p>;

  return (
    <div className="space-y-6" data-testid="landing-settings">
      {/* ---- Logo ---- */}
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <Head title="Logo Usaha" desc="Dipakai di halaman pembuka (landing page). Format PNG/JPG/WEBP, latar transparan lebih bagus. Otomatis dikecilkan maksimal 512px." />
        <div className="flex flex-wrap items-center gap-4">
          <div className="grid h-20 w-20 place-items-center rounded-2xl border border-border bg-secondary/40">
            {logo ? (
              <img src={logo} alt="Logo" className="h-16 w-16 object-contain" data-testid="landing-logo-preview" />
            ) : (
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={logoRef} type="file" accept="image/*" onChange={onLogoPick} className="hidden" id="logo-file" data-testid="landing-logo-input" />
            <Button type="button" variant="outline" className="rounded-full" onClick={() => logoRef.current?.click()} disabled={uploading === "logo"} data-testid="landing-logo-upload">
              {uploading === "logo" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Unggah Logo
            </Button>
            {logo && (
              <Button type="button" variant="outline" className="rounded-full border-rose-500/40 text-rose-300" onClick={() => setLogo("")} data-testid="landing-logo-remove">
                <Trash2 className="mr-1.5 h-4 w-4" /> Hapus Logo
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ---- Galeri ---- */}
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <Head title="Galeri Screenshot Dasbor" desc={`Tampil di landing page sebagai bukti tampilan aplikasi. Maksimal ${MAX_GALLERY} gambar; gambar besar otomatis dikompres.`}>
          <div className="flex flex-wrap gap-2">
            <input ref={galRef} type="file" accept="image/*" multiple onChange={onGalleryPick} className="hidden" data-testid="landing-gallery-input" />
            <Button type="button" variant="outline" className="rounded-full" onClick={() => galRef.current?.click()} disabled={uploading === "gallery" || gallery.length >= MAX_GALLERY} data-testid="landing-gallery-upload">
              {uploading === "gallery" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Tambah Gambar
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={reset} disabled={busy} data-testid="landing-settings-reset">
              <RotateCcw className="mr-1.5 h-4 w-4" /> Contoh Bawaan
            </Button>
          </div>
        </Head>
        {gallery.length === 0 ? (
          <p className="text-xs text-muted-foreground">Belum ada gambar. Bagian galeri tidak akan tampil di landing page.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((g, i) => (
              <div key={`${g.image.slice(-24)}-${i}`} className="space-y-2 rounded-xl border border-border bg-secondary/30 p-3" data-testid={`gallery-row-${i}`}>
                <img src={g.image} alt={g.title || `Galeri ${i + 1}`} className="h-32 w-full rounded-lg object-cover object-top" />
                <Input
                  value={g.title || ""} placeholder="Judul gambar" data-testid={`gallery-title-${i}`}
                  onChange={(e) => setGallery(gallery.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                  className="h-9 rounded-lg bg-background/60 text-xs"
                />
                <div className="flex justify-between gap-2">
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg px-2 text-[11px]" disabled={i === 0}
                      onClick={() => { const g2 = [...gallery]; [g2[i - 1], g2[i]] = [g2[i], g2[i - 1]]; setGallery(g2); }} data-testid={`gallery-up-${i}`}>↑</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg px-2 text-[11px]" disabled={i === gallery.length - 1}
                      onClick={() => { const g2 = [...gallery]; [g2[i + 1], g2[i]] = [g2[i], g2[i + 1]]; setGallery(g2); }} data-testid={`gallery-down-${i}`}>↓</Button>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg border-rose-500/40 px-2 text-[11px] text-rose-300"
                    onClick={() => setGallery(gallery.filter((_, j) => j !== i))} data-testid={`gallery-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- Testimoni ---- */}
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <Head title="Testimoni Pelanggan" desc={`Tampil di landing page. Maksimal ${MAX_TESTIMONIALS} testimoni. Kosongkan isi testimoni untuk menghapus otomatis saat disimpan.`}>
          <Button type="button" variant="outline" className="rounded-full" data-testid="testimonial-add"
            disabled={testimonials.length >= MAX_TESTIMONIALS}
            onClick={() => setTestimonials([...testimonials, { name: "", city: "", text: "", rating: 5 }])}>
            <Plus className="mr-1.5 h-4 w-4" /> Tambah Testimoni
          </Button>
        </Head>
        {testimonials.length === 0 ? (
          <p className="text-xs text-muted-foreground">Belum ada testimoni.</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {testimonials.map((t, i) => (
              <div key={`tst-${i}`} className="space-y-3 rounded-xl border border-border bg-secondary/30 p-4" data-testid={`testimonial-row-${i}`}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Nama</Label>
                    <Input value={t.name || ""} data-testid={`testimonial-name-${i}`} placeholder="Pak Yanto"
                      onChange={(e) => setTestimonials(testimonials.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                      className="h-9 rounded-lg bg-background/60 text-xs" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Kota / Usaha</Label>
                    <Input value={t.city || ""} data-testid={`testimonial-city-${i}`} placeholder="Tegal, Jawa Tengah"
                      onChange={(e) => setTestimonials(testimonials.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)))}
                      className="h-9 rounded-lg bg-background/60 text-xs" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Isi Testimoni</Label>
                  <Textarea value={t.text || ""} rows={3} data-testid={`testimonial-text-${i}`} placeholder="Tulis pengalaman pelanggan…"
                    onChange={(e) => setTestimonials(testimonials.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                    className="rounded-lg bg-background/60 text-xs" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button key={s} type="button" aria-label={`${s} bintang`} data-testid={`testimonial-rating-${i}-${s}`}
                        onClick={() => setTestimonials(testimonials.map((x, j) => (j === i ? { ...x, rating: s } : x)))}>
                        <Star className={`h-4 w-4 ${s <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                      </button>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg border-rose-500/40 text-[11px] text-rose-300"
                    onClick={() => setTestimonials(testimonials.filter((_, j) => j !== i))} data-testid={`testimonial-remove-${i}`}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Hapus
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- Nomor CS ---- */}
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <Head title="Nomor CS WhatsApp (rotasi otomatis)" desc={`Tombol WA di landing page memakai nomor-nomor ini secara bergiliran (round-robin) supaya pesan terbagi rata. Maksimal ${MAX_CS} nomor. Kalau dikosongkan, dipakai nomor CS di tab Pengaturan Umum.`}>
          <Button type="button" variant="outline" className="rounded-full" data-testid="cs-add"
            disabled={csNumbers.length >= MAX_CS}
            onClick={() => setCsNumbers([...csNumbers, { name: "", number: "" }])}>
            <Plus className="mr-1.5 h-4 w-4" /> Tambah Nomor CS
          </Button>
        </Head>
        {csNumbers.length === 0 ? (
          <p className="text-xs text-muted-foreground">Belum ada nomor CS khusus — memakai nomor CS umum.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {csNumbers.map((c, i) => (
              <div key={`cs-${i}`} className="flex items-end gap-2 rounded-xl border border-border bg-secondary/30 p-3" data-testid={`cs-row-${i}`}>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Nama CS</Label>
                  <Input value={c.name || ""} placeholder="Andi" data-testid={`cs-name-${i}`}
                    onChange={(e) => setCsNumbers(csNumbers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    className="h-9 rounded-lg bg-background/60 text-xs" />
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Nomor WA</Label>
                  <Input value={c.number || ""} placeholder="08xxxxxxxxxx" data-testid={`cs-number-${i}`}
                    onChange={(e) => setCsNumbers(csNumbers.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))}
                    className="h-9 rounded-lg bg-background/60 font-mono text-xs" />
                </div>
                <Button type="button" size="sm" variant="outline" className="h-9 rounded-lg border-rose-500/40 px-2 text-rose-300"
                  onClick={() => setCsNumbers(csNumbers.filter((_, j) => j !== i))} data-testid={`cs-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={save} disabled={busy} data-testid="landing-settings-save"
          className="h-11 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-7 text-white hover:opacity-90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Simpan Konten Landing</>}
        </Button>
        <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground underline-offset-4 hover:text-violet-300 hover:underline" data-testid="landing-preview-link">
          Lihat landing page →
        </a>
      </div>
    </div>
  );
}
